import { StrictMode, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/react';
import { App } from './streamit';
import { SessionProvider } from '../features/session';
import { RemoteProvider } from '../features/remote';
import { AuthScreen } from '../features/auth';
import { isAuthLocation } from '../lib/auth-routing';
import { captureInvitation } from '../lib/invitation';
import { InvitationScreen } from '../features/invitation';
import { captureDeletionReceipt } from '../lib/deletion-receipt';
import { DeletionStatusScreen } from '../features/account-deletion-status';
import './globals.css';
import '../features/display-settings.css';
import { startDisplayPreferences } from '../lib/display-preferences';
startDisplayPreferences();
const key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const initialInvitation = captureInvitation(location.href);
const initialDeletionReceipt = captureDeletionReceipt(location.href);
const publicDeletionPage = location.pathname === '/account-deletion';
if (publicDeletionPage && (location.hash || location.search)) history.replaceState(history.state, '', '/account-deletion');
if (location.pathname === '/invite' && (location.hash || location.search))
  history.replaceState(history.state, '', '/invite');
function subscribeLocation(notify: () => void) {
  addEventListener('hashchange', notify);
  addEventListener('popstate', notify);
  return () => {
    removeEventListener('hashchange', notify);
    removeEventListener('popstate', notify);
  };
}
const readLocation = () =>
  `${location.pathname}${location.search}${location.hash}`;
function CurrentScreen() {
  const path = useSyncExternalStore(subscribeLocation, readLocation);
  const url = new URL(path, location.origin);
  if (url.pathname === '/account-deletion') return <DeletionStatusScreen initial={initialDeletionReceipt} />;
  if (url.pathname === '/invite') return <InvitationScreen configured={Boolean(key)} initialToken={initialInvitation} />;
  return isAuthLocation(url.pathname, url.hash) ? (
    <AuthScreen configured={Boolean(key)} />
  ) : (
    <App authConfigured={Boolean(key)} />
  );
}
const app = <CurrentScreen />;
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {key && !publicDeletionPage ? (
      <ClerkProvider
        publishableKey={key}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        afterSignOutUrl="/"
        appearance={{
          // Marquee palette (app/theme.css). Appearance only; the Clerk flow,
          // domains and redirects are unchanged.
          variables: {
            colorPrimary: '#F3E8D6', colorPrimaryForeground: '#060B0C',
            colorBackground: '#152326', colorForeground: '#F3E8D6', colorMutedForeground: '#A7B3B1',
            colorInput: '#0D1719', colorInputForeground: '#F3E8D6', colorBorder: '#24373A', colorRing: '#FF5C47',
            colorDanger: '#FF5C47', colorSuccess: '#3FA79F', colorNeutral: '#F3E8D6', colorModalBackdrop: '#060B0CB3',
          },
        }}
      >
        <SessionProvider>
          <RemoteProvider>{app}</RemoteProvider>
        </SessionProvider>
      </ClerkProvider>
    ) : (
      app
    )}
  </StrictMode>,
);
if (import.meta.env.PROD && 'serviceWorker' in navigator)
  navigator.serviceWorker.register('/sw.js').catch(() => {});
